import React from 'react';
import { SwipeableDrawer, Box, List } from '@mui/material';
import NavigationItem from './NavigationItem';
import CollapsibleNavigationItem from './CollapsibleNavigationItem';
import TopNavigationIcon from './TopNavigationIcon';
import ImpersonationControls from './ImpersonationControls';
import UserAvatarItem from './UserAvatarItem';

/**
 * MobileDrawer component for rendering the mobile sidebar drawer
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onOpen - Function to open the drawer
 * @param {Function} props.onClose - Function to close the drawer
 * @param {Array} props.navigationItems - Array of navigation items
 * @param {Object} props.openCollapsible - Object tracking which collapsible items are open
 * @param {Function} props.onToggleCollapsible - Function to toggle collapsible items
 * @param {boolean} props.isImpersonating - Whether user impersonation is active
 * @param {Object} props.user - Current user
 * @param {Object} props.originalUser - Original user when impersonating
 * @param {boolean} props.hasAdminAccess - Whether the user has admin access
 * @param {Function} props.onStartImpersonation - Function to start impersonation
 * @param {Function} props.onStopImpersonation - Function to stop impersonation
 * @param {Function} props.onLogout - Function to handle logout
 * @returns {JSX.Element} MobileDrawer component
 */
const MobileDrawer = ({
  open,
  onOpen,
  onClose,
  navigationItems,
  openCollapsible,
  onToggleCollapsible,
  isImpersonating,
  user,
  originalUser,
  hasAdminAccess,
  onStartImpersonation,
  onStopImpersonation,
  onLogout
}) => {
  // Filter navigation items based on permissions
  const filteredItems = navigationItems.filter(item => {
    // Skip items that require admin if user doesn't have admin access
    if (item.requiredPermission === 'admin' && !hasAdminAccess) {
      return false;
    }
    // Skip desktop-only items
    if (item.desktopOnly) {
      return false;
    }
    return true;
  });

  // Find the top icon item
  const topIconItem = filteredItems.find(item => item.isTopIcon);
  
  // Regular navigation items (excluding top icon)
  const regularItems = filteredItems.filter(item => !item.isTopIcon);

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 'var(--sidebar-expanded-width)',
          height: 'calc(100dvh - var(--topnavbar-height))',
          boxSizing: 'border-box',
          top: 'var(--topnavbar-height)',
          borderRadius: '0',
          bgcolor: 'transparent',
          borderWidth: '0',
          p: 1.5,
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'var(--bg-panel)',
          backdropFilter: 'var(--effect-panel-backdrop-filter)',
          border: 'var(--border-width) solid',
          borderColor: 'var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        {/* Top portion: menu items */}
        <Box sx={{ p: 1.25, flexGrow: 1, overflowY: 'auto' }}>
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 0 }}>
            {/* Top icon - only for navigation to chat */}
            {topIconItem && (
              <TopNavigationIcon
                item={topIconItem}
                miniDrawer={false}
                isDesktop={false}
                onClick={onClose}
              />
            )}

            {/* Regular navigation items */}
            {regularItems.map((item) => (
              item.isCollapsible ? (
                <CollapsibleNavigationItem
                  key={item.id}
                  item={item}
                  miniDrawer={false}
                  isDesktop={false}
                  open={openCollapsible[item.id] || false}
                  onToggle={() => onToggleCollapsible(item.id)}
                  onItemClick={onClose}
                />
              ) : (
                <NavigationItem
                  key={item.id}
                  item={item}
                  miniDrawer={false}
                  isDesktop={false}
                  onClick={onClose}
                />
              )
            ))}
          </List>
        </Box>

        {/* Bottom portion: impersonation controls and logout */}
        <Box sx={{ p: 1.25 }}>
          <ImpersonationControls
            isImpersonating={isImpersonating}
            hasAdminAccess={hasAdminAccess}
            miniDrawer={false}
            isDesktop={false}
            onStartImpersonation={onStartImpersonation}
            onStopImpersonation={onStopImpersonation}
          />

          {/* User avatar with dropdown menu */}
          <UserAvatarItem
            user={user}
            miniDrawer={false}
            isDesktop={false}
            onLogout={onLogout}
          />
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

export default MobileDrawer;
